import { BLOOD_GROUPS } from '@felix/domain';
import {
  Alert,
  Autocomplete,
  Button,
  FileButton,
  Select,
  Skeleton,
  Textarea,
  TextInput,
} from '@mantine/core';
import { IconLock, IconPhoto, IconTrash } from '@tabler/icons-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Panel } from '../../Panel.js';
import { PersonAvatar } from '../../PersonAvatar.js';
import dialog from './CrewCardDialog.module.css';
import classes from './CrewParticulars.module.css';
import profileClasses from './CrewProfileScreen.module.css';
import {
  CLOTHING_SIZES,
  type CrewCardForm,
  type CrewMedicalForm,
  crewCardErrorKey,
  medicalChanged,
  medicalFormOf,
  medicalInput,
} from './crew-profile.js';
import { type CrewCardEditor, errorCode } from './source.js';
import type {
  CrewMedicalRecord,
  CrewMedicalResult,
  CrewProfileRecord,
  CrewProfileView,
} from './types.js';

/**
 * Personel kartının fotoğrafı, kıyafet ve fiziksel özellikleri, sağlık bilgisi (sahip 2026-11-04
 * B1, B2; ADR 0158). Pencerenin sekmeleri ve Crew Profile'ın bölümleri.
 */

function useErrorText() {
  const { t } = useTranslation();
  return (error: unknown) =>
    t(crewCardErrorKey(errorCode(error)), { defaultValue: t('crewProfile.errors.GENERIC') });
}

/**
 * Pencerede fotoğraf (ADR 0158 §1): seçilen dosya hemen yüklenir ve karta konur; kaldırma da
 * hemen. Kartı olmayan kişide (henüz kaydedilmemiş) düğme yok, not var.
 */
export function CrewPhotoField({
  view,
  setPhoto,
  onChange,
}: {
  view: CrewProfileView;
  setPhoto: NonNullable<CrewCardEditor['setPhoto']>;
  onChange: (view: CrewProfileView) => void;
}) {
  const { t } = useTranslation();
  const p = (key: string) => t(`crewProfile.photo.${key}`);
  const errorText = useErrorText();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rowVersion = view.profile?.rowVersion ?? null;
  const run = async (file: File | null) => {
    if (rowVersion === null) return;
    setBusy(true);
    setError(null);
    try {
      onChange(await setPhoto(view.userId, rowVersion, file));
    } catch (caught) {
      setError(errorText(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className={classes.photo}>
      <PersonAvatar name={view.fullName} size={72} src={view.photoUrl ?? null} />
      <div className={classes.photoBody}>
        <span className={classes.photoLabel}>{p('label')}</span>
        {rowVersion === null ? (
          <span className={dialog.help}>{p('needsCard')}</span>
        ) : (
          <>
            <div className={classes.photoActions}>
              <FileButton accept="image/jpeg,image/png,image/webp" onChange={(file) => run(file)}>
                {(props) => (
                  <Button
                    {...props}
                    size="xs"
                    variant="default"
                    loading={busy}
                    leftSection={<IconPhoto size={14} />}
                  >
                    {view.photoUrl ? p('change') : p('upload')}
                  </Button>
                )}
              </FileButton>
              {view.photoUrl ? (
                <Button
                  size="xs"
                  variant="subtle"
                  color="red"
                  disabled={busy}
                  leftSection={<IconTrash size={14} />}
                  onClick={() => run(null)}
                >
                  {p('remove')}
                </Button>
              ) : null}
            </div>
            <span className={dialog.help}>{p('help')}</span>
          </>
        )}
        {error ? (
          <p className={dialog.error} role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </div>
  );
}

/** "Clothing & physical" sekmesi (ADR 0158): beden ve numara serbest metin, öneriyle. */
export function CrewPhysicalFields({
  form,
  onChange,
}: {
  form: CrewCardForm;
  onChange: (form: CrewCardForm) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`crewProfile.fields.${key}`);
  const d = (key: string) => t(`crewProfile.physical.${key}`);
  const set = (key: keyof CrewCardForm) => (value: string) => onChange({ ...form, [key]: value });
  const size = (key: 'coverallSize' | 'upperSize' | 'lowerSize') => (
    <Autocomplete
      label={f(key)}
      placeholder={d('sizePlaceholder')}
      data={[...CLOTHING_SIZES]}
      maxLength={12}
      value={form[key]}
      onChange={set(key)}
      comboboxProps={{ withinPortal: true }}
    />
  );
  const text = (key: keyof CrewCardForm, extra: Record<string, unknown> = {}) => (
    <TextInput
      label={f(key)}
      value={String(form[key])}
      onChange={(event) => set(key)(event.currentTarget.value)}
      {...extra}
    />
  );
  return (
    <>
      <fieldset className={dialog.group}>
        <legend className={dialog.legend}>{d('clothing')}</legend>
        <div className={dialog.grid}>
          {size('coverallSize')}
          {size('upperSize')}
          {size('lowerSize')}
          {text('shoeSize', { maxLength: 12, placeholder: d('shoePlaceholder') })}
        </div>
      </fieldset>
      <fieldset className={dialog.group}>
        <legend className={dialog.legend}>{d('physical')}</legend>
        <div className={dialog.grid}>
          {text('heightCm', { inputMode: 'numeric', maxLength: 3, description: d('heightHelp') })}
          {text('weightKg', { inputMode: 'decimal', maxLength: 5, description: d('weightHelp') })}
          {text('eyeColor', { maxLength: 40 })}
          {text('hairColor', { maxLength: 40 })}
          <Textarea
            className={dialog.wide}
            label={f('distinguishingMarks')}
            value={form.distinguishingMarks}
            maxLength={300}
            autosize
            minRows={2}
            onChange={(event) => set('distinguishingMarks')(event.currentTarget.value)}
          />
        </div>
      </fieldset>
    </>
  );
}

/** KVKK notu: özel nitelikli veri, yalnız yetkili, her görüntüleme kayıtlı. */
function MedicalNotice() {
  const { t } = useTranslation();
  return (
    <Alert variant="light" color="gray" icon={<IconLock size={16} />} className={classes.notice}>
      {t('crewProfile.medical.notice')}
    </Alert>
  );
}

/** Sağlık alanları (düzenleme ya da salt okunur). */
function MedicalFields({
  form,
  readOnly,
  onChange,
}: {
  form: CrewMedicalForm;
  readOnly: boolean;
  onChange: (form: CrewMedicalForm) => void;
}) {
  const { t } = useTranslation();
  const f = (key: string) => t(`crewProfile.fields.${key}`);
  const area = (key: 'medicalHistory' | 'medications' | 'allergies', max: number) => (
    <Textarea
      className={dialog.wide}
      label={f(key)}
      value={form[key]}
      maxLength={max}
      autosize
      minRows={key === 'medicalHistory' ? 3 : 2}
      maxRows={10}
      readOnly={readOnly}
      onChange={(event) => onChange({ ...form, [key]: event.currentTarget.value })}
    />
  );
  return (
    <div className={dialog.grid}>
      <Select
        label={f('bloodGroup')}
        data={[...BLOOD_GROUPS]}
        clearable={!readOnly}
        readOnly={readOnly}
        value={form.bloodGroup || null}
        onChange={(value) => onChange({ ...form, bloodGroup: value ?? '' })}
        comboboxProps={{ withinPortal: true }}
      />
      {area('medicalHistory', 4000)}
      {area('medications', 2000)}
      {area('allergies', 2000)}
    </div>
  );
}

type MedicalState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'ready'; record: CrewMedicalRecord | null; canEdit: boolean };

/**
 * Pencerenin "Medical" sekmesi (ADR 0158 §2): sekme açılınca okunur (okuma kayıtlıdır); kendi
 * Kaydet düğmesi vardır, kart alanlarından ayrı yazılır (ayrı izin).
 */
export function CrewMedicalEditor({
  userId,
  load,
  save,
}: {
  userId: string;
  load: NonNullable<CrewCardEditor['medical']>;
  save?: CrewCardEditor['saveMedical'];
}) {
  const { t } = useTranslation();
  const m = (key: string) => t(`crewProfile.medical.${key}`);
  const errorText = useErrorText();
  const [state, setState] = useState<MedicalState | null>(null);
  const [form, setForm] = useState<CrewMedicalForm>(() => medicalFormOf(null));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const apply = (result: CrewMedicalResult) => {
    setState({ kind: 'ready', record: result.medical, canEdit: result.canEdit && !!save });
    setForm(medicalFormOf(result.medical));
  };
  // Okuma kayda geçer: kişi başına bir kez, sekme ilk açıldığında (yeniden çizim okumaz)
  const loader = useRef({ load, apply, errorText });
  loader.current = { load, apply, errorText };
  useEffect(() => {
    let live = true;
    setState({ kind: 'loading' });
    loader.current
      .load(userId)
      .then((result) => {
        if (live) loader.current.apply(result);
      })
      .catch((caught: unknown) => {
        if (live) setState({ kind: 'error', message: loader.current.errorText(caught) });
      });
    return () => {
      live = false;
    };
  }, [userId]);
  if (state === null || state.kind === 'loading') {
    return <Skeleton height={160} radius="md" aria-label={m('loading')} />;
  }
  if (state.kind === 'error') {
    return (
      <p className={dialog.error} role="alert">
        {state.message}
      </p>
    );
  }
  const { record, canEdit } = state;
  const dirty = medicalChanged(form, record);
  return (
    <div className={dialog.form}>
      <MedicalNotice />
      <MedicalFields form={form} readOnly={!canEdit} onChange={setForm} />
      {canEdit ? null : <span className={dialog.help}>{m('readOnly')}</span>}
      {message ? (
        <p
          className={message.ok ? dialog.help : dialog.error}
          role={message.ok ? 'status' : 'alert'}
        >
          {message.text}
        </p>
      ) : null}
      {canEdit && save ? (
        <div className={dialog.actions}>
          <Button
            type="button"
            loading={busy}
            disabled={!dirty}
            onClick={async () => {
              setBusy(true);
              setMessage(null);
              try {
                apply(await save(userId, medicalInput(form, record)));
                setMessage({ ok: true, text: m('saved') });
              } catch (caught) {
                setMessage({ ok: false, text: errorText(caught) });
              } finally {
                setBusy(false);
              }
            }}
          >
            {m('save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/** Etiket ve değer satırı (Crew Profile'ın `Field`'ı ile aynı görünüm). */
function Row({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className={profileClasses.field}>
      <dt>{label}</dt>
      <dd className={classes.value}>
        {value === null || value === undefined || value === '' ? '—' : value}
      </dd>
    </div>
  );
}

/** Crew Profile ▸ Overview: "Clothing & Physical" bölümü (yalnız ofiste; ADR 0158). */
export function PhysicalPanel({ profile }: { profile: CrewProfileRecord | null }) {
  const { t } = useTranslation();
  const f = (key: string) => t(`crewProfile.fields.${key}`);
  const unit = (value: string | null | undefined, key: 'cm' | 'kg') =>
    value ? t(`crewProfile.physical.${key}`, { value }) : null;
  return (
    <Panel title={t('crewProfile.sections.physical')} divider>
      <dl className={profileClasses.fields}>
        <Row label={f('coverallSize')} value={profile?.coverallSize} />
        <Row label={f('upperSize')} value={profile?.upperSize} />
        <Row label={f('lowerSize')} value={profile?.lowerSize} />
        <Row label={f('shoeSize')} value={profile?.shoeSize} />
        <Row label={f('heightCm')} value={unit(profile?.heightCm, 'cm')} />
        <Row label={f('weightKg')} value={unit(profile?.weightKg, 'kg')} />
        <Row label={f('eyeColor')} value={profile?.eyeColor} />
        <Row label={f('hairColor')} value={profile?.hairColor} />
        <Row label={f('distinguishingMarks')} value={profile?.distinguishingMarks} />
      </dl>
    </Panel>
  );
}

/**
 * Crew Profile ▸ Overview: "Medical Information" (ADR 0158 §2). Bilgi açılmadan gösterilmez;
 * "Show" okumayı yapar ve okuma sunucuda kayda geçer [KVKK md. 6, md. 12].
 */
export function MedicalPanel({ load }: { load: () => Promise<CrewMedicalResult> }) {
  const { t } = useTranslation();
  const m = (key: string) => t(`crewProfile.medical.${key}`);
  const f = (key: string) => t(`crewProfile.fields.${key}`);
  const errorText = useErrorText();
  const [state, setState] = useState<MedicalState | null>(null);
  const show = async () => {
    setState({ kind: 'loading' });
    try {
      const result = await load();
      setState({ kind: 'ready', record: result.medical, canEdit: result.canEdit });
    } catch (caught) {
      setState({ kind: 'error', message: errorText(caught) });
    }
  };
  const record = state?.kind === 'ready' ? state.record : null;
  return (
    <Panel
      title={t('crewProfile.sections.medical')}
      divider
      extra={
        state?.kind === 'ready' ? (
          <Button size="xs" variant="subtle" onClick={() => setState(null)}>
            {m('hide')}
          </Button>
        ) : null
      }
    >
      {state === null ? (
        <div className={classes.hidden}>
          <span className={dialog.help}>{m('hiddenHelp')}</span>
          <Button size="xs" variant="default" leftSection={<IconLock size={14} />} onClick={show}>
            {m('show')}
          </Button>
        </div>
      ) : state.kind === 'loading' ? (
        <Skeleton height={120} radius="md" aria-label={m('loading')} />
      ) : state.kind === 'error' ? (
        <p className={dialog.error} role="alert">
          {state.message}
        </p>
      ) : record === null ? (
        <p className={dialog.help}>{m('empty')}</p>
      ) : (
        <dl className={profileClasses.fields}>
          <Row label={f('bloodGroup')} value={record.bloodGroup} />
          <Row label={f('medicalHistory')} value={record.medicalHistory} />
          <Row label={f('medications')} value={record.medications} />
          <Row label={f('allergies')} value={record.allergies} />
        </dl>
      )}
    </Panel>
  );
}
